import { useEffect, useState } from 'react'
import { useUser } from '@clerk/react'
import { Link } from 'react-router-dom'

const DISMISS_KEY = 'fv_low_credits_dismissed_at'
const DISMISS_HOURS = 6
const LOW_THRESHOLD = 50

// Top-of-page banner that appears when the user's credit balance falls
// below LOW_THRESHOLD. Uses frank-system-warning as the avatar so the
// warning has a face. Dismissible per-session — once dismissed, stays
// hidden for DISMISS_HOURS so we're not nagging on every page load.
export default function LowCreditsBanner() {
  const { user, isLoaded } = useUser()
  const [dismissed, setDismissed] = useState(false)

  // Read dismissal state on mount
  useEffect(() => {
    if (typeof window === 'undefined') return
    const raw = window.localStorage.getItem(DISMISS_KEY)
    if (!raw) return
    const parsed = parseInt(raw, 10)
    if (!Number.isFinite(parsed)) return
    const ageMs = Date.now() - parsed
    if (ageMs < DISMISS_HOURS * 60 * 60 * 1000) {
      setDismissed(true)
    }
  }, [])

  if (!isLoaded || dismissed) return null

  const credits = (user?.publicMetadata?.credits as number | undefined) ?? null
  if (credits === null || credits >= LOW_THRESHOLD) return null

  const handleDismiss = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()))
    }
    setDismissed(true)
  }

  return (
    <div className="fixed top-0 left-[var(--sidebar-width)] right-0 z-30 px-4 pt-3 max-md:left-0 max-md:pt-16 pointer-events-none">
      <div className="max-w-3xl mx-auto pointer-events-auto">
        <div className="rounded-xl border border-warning/40 bg-bg-card/95 backdrop-blur-md shadow-xl shadow-black/40 p-3 flex items-center gap-3">
          <img
            src="/frank-system-warning.png"
            alt="Frank pointing at a low-credits warning"
            className="w-14 h-14 rounded-lg object-cover bg-bg-secondary border border-border shrink-0 select-none"
            draggable={false}
          />
          <div className="flex-1 min-w-0 space-y-0.5">
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-warning">
              ● system warning · low credits
            </p>
            <p className="text-sm text-text-primary">
              <span className="italic text-text-secondary">Darling, your wallet is</span>{' '}
              <span className="font-semibold text-warning">
                {credits.toLocaleString()} credit{credits === 1 ? '' : 's'}
              </span>{' '}
              <span className="italic text-text-secondary">
                from a quiet evening. Frank suggests a top-up.
              </span>
            </p>
          </div>
          <Link
            to="/settings"
            className="font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 rounded-lg bg-neon text-bg-primary font-semibold hover:bg-neon-dim transition-colors cursor-pointer shrink-0"
          >
            Top up
          </Link>
          <button
            onClick={handleDismiss}
            className="text-text-muted hover:text-text-primary p-1 cursor-pointer shrink-0"
            aria-label="Dismiss"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
